import { Injectable, OnDestroy } from '@angular/core';
import { AppConfigService } from '../../services/app-config.service';
import { LayersInfoService } from './layers.service';
import { ScreenService } from '../../services/screen.service';
import { ToastService } from '../../services/toast.service';
import { TranslationService } from '../../services/translation.service';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  asapScheduler,
  asyncScheduler,
  catchError,
  defaultIfEmpty,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import { ArrayUtils } from '../../utils/array.utils';
import orderBy from 'lodash/orderBy';
import { LoadingController } from '../../helpers/loading.controller';
import { EsriUtils } from '../../utils/esri.utils';
import { GeometryUtils } from '../../utils/geometry.utils';
import RelationshipQuery from '@arcgis/core/rest/support/RelationshipQuery';
import { MainMapService } from './main-map.service';
import { WidgetModalType } from '../../models/widget-modal-type';

export interface NetworkResultItemLike<T extends NetworkResultItemLike<T>> {
  id: number;
  random?: number;
  operator: string;
  downloadSpeed: string;
  children?: T[];
}

export interface FixeNetworkResultItemLike
  extends NetworkResultItemLike<FixeNetworkResultItemLike> {
  technology: string;
  technologyCode: number;
  uploadSpeed: string;
}

export interface MobileNetworkResultItemLike
  extends NetworkResultItemLike<MobileNetworkResultItemLike> {
  technology: string;
  technologyCode: string;
  downloadSpeedCode: number;
}

export interface SatelliteNetworkResultItemLike
  extends NetworkResultItemLike<SatelliteNetworkResultItemLike> {
  uploadSpeed: string;
}

@Injectable()
export class AvailableCoverageService implements OnDestroy {
  private _fixeNetworkGeomLayer: FeatureLayer;
  private get fixeNetworkGeomLayer() {
    if (!this._fixeNetworkGeomLayer) {
      this._fixeNetworkGeomLayer = this._handleFeatureLayer(
        '~fixeAvailableCoverageService'
      );
    }

    return this._fixeNetworkGeomLayer;
  }

  private _mobileNetworkGeomLayer: FeatureLayer;
  private get mobileNetworkGeomLayer() {
    if (!this._mobileNetworkGeomLayer) {
      this._mobileNetworkGeomLayer = this._handleFeatureLayer(
        '~mobileAvailableCoverageService'
      );
    }

    return this._mobileNetworkGeomLayer;
  }

  private _satelliteNetworkGeomLayer: FeatureLayer;
  private get satelliteNetworkGeomLayer() {
    if (!this._satelliteNetworkGeomLayer) {
      this._satelliteNetworkGeomLayer = this._handleFeatureLayer(
        '~satelliteAvailableCoverageService'
      );
    }

    return this._satelliteNetworkGeomLayer;
  }

  private loadingController = new LoadingController();

  get loadingChanges() {
    return this.loadingController.changes;
  }

  private _pointSubject = new BehaviorSubject<__esri.Point>(null);

  private _resultsSubject = new BehaviorSubject<{
    fixeNetworkItems?: FixeNetworkResultItemLike[];
    mobileNetworkItems?: MobileNetworkResultItemLike[];
    satelliteNetworkItems?: SatelliteNetworkResultItemLike[];
  }>(null);

  get resultsChanged() {
    return this._resultsSubject.asObservable();
  }

  private lifecycle = new Subject<void>();

  private initialized = false;

  constructor(
    private appConfig: AppConfigService,
    private mainMapService: MainMapService,
    private layersInfoService: LayersInfoService,
    private toastService: ToastService,
    private translationService: TranslationService
  ) {}

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    EsriUtils.destroyLayersRecursive([
      this._fixeNetworkGeomLayer,
      this._mobileNetworkGeomLayer,
      this._satelliteNetworkGeomLayer,
    ]);
  }

  setPoint(point: __esri.Point) {
    this._pointSubject.next(point);

    if (!this.initialized) {
      this.initialized = true;
      this.setup();
    }
  }

  private setup() {
    this._pointSubject
      .pipe(
        switchMap((point) => {
          if (!point) {
            this.mainMapService.closeWidgetModal(
              WidgetModalType.AvailableCoverage
            );
            this._resultsSubject.next(null);
            return EMPTY;
          }

          const aux: [Observable<any[]>, string][] = [
            [
              this.loadFixeNetworkResults(point),
              this.translationService.getCurrentLanguage()['error-load-fixed-network'],
            ],
            [
              this.loadMobileNetworkResults(point),
              this.translationService.getCurrentLanguage()['error-load-mobile-network'],
            ],
            [
              this.loadSatelliteNetworkResults(point),
              this.translationService.getCurrentLanguage()['error-load-satellite'],
            ],
          ];

          this.loadingController.show();
          return zip(
            aux.map(([o, msg]) => {
              return o.pipe(
                catchError((response) => {
                  if (response) {
                    this.toastService.error(msg);
                  }

                  return of(null);
                })
              );
            })
          ).pipe(
            finalize(() => {
              this.loadingController.hide();
            }),
            tap(
              ([
                fixeNetworkItems,
                mobileNetworkItems,
                satelliteNetworkItems,
              ]) => {
                if (
                  (fixeNetworkItems ||
                    mobileNetworkItems ||
                    satelliteNetworkItems) &&
                  !(
                    fixeNetworkItems?.length ||
                    mobileNetworkItems?.length ||
                    satelliteNetworkItems?.length
                  )
                ) {
                  this.toastService.warning(this.translationService.getCurrentLanguage()['no-data']);
                } else {
                  this.mainMapService.openWidgetModal(
                    WidgetModalType.AvailableCoverage
                  );
                }

                this._resultsSubject.next({
                  fixeNetworkItems,
                  mobileNetworkItems,
                  satelliteNetworkItems,
                });
              }
            )
          );
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private loadFixeNetworkResults(
    point: __esri.Point
  ): Observable<FixeNetworkResultItemLike[]> {
    const fieldStr = 'objectid';

    const layer = this.fixeNetworkGeomLayer;
    const query = layer.createQuery();
    query.geometry = point;
    query.distance = 35;
    query.units = 'meters';
    query.outFields = [fieldStr];
    query.returnGeometry = true;

    return EsriUtils.queryFeatures(layer, query).pipe(
      mergeMap(({ features }) => {
        if (features?.length) {
          let featuresOrdered = features.map((graphic) => {
            const distance = GeometryUtils.distance(point, graphic.geometry);

            const code = graphic.attributes[fieldStr];

            return { distance, code };
          });
          featuresOrdered = orderBy(featuresOrdered, (x) => x.distance);

          const codes = featuresOrdered.map((x) => x.code);
          return this.listFixeResultsRelated(codes);
        }

        return of([]);
      })
    );
  }

  private listFixeResultsRelated(
    codes: any[]
  ): Observable<FixeNetworkResultItemLike[]> {
    const layer = this.fixeNetworkGeomLayer;

    const techFieldsCfgList = [
      ['tecnologia_a', 'vel_max_dl_a', 'vel_max_ul_a'],
      ['tecnologia_b', 'vel_max_dl_b', 'vel_max_ul_b'],
    ];

    const outFields = ['objectid', 'operador', ...techFieldsCfgList.flat()];

    const orderByFields = [
      ...['vel_max_dl_a', 'vel_max_dl_b', 'vel_max_ul_a', 'vel_max_ul_b'].map(
        (x) => {
          return `${x} DESC`;
        }
      ),
    ];

    const techCodesOrder = [1, 2, 4, 3, 6, 5, 7, 8, 9];
    const orderResults = (itemList: FixeNetworkResultItemLike[]) => {
      return techCodesOrder.flatMap((code) => {
        const list = itemList.filter((x) => x.technologyCode === code);

        return orderBy(
          list,
          ['downloadSpeed', 'uploadSpeed', 'random'],
          ['desc', 'desc', 'asc']
        );
      });
    };

    const whereStr = techFieldsCfgList
      .map(([_, downloadField, uploadField]) => {
        return `(${[downloadField, uploadField]
          .map((field) => {
            return `(${field} is not null and ${field} <> 0)`;
          })
          .join(' and ')})`;
      })
      .join(' or ');

    const observables = layer.relationships.map((rel) => {
      const query = new RelationshipQuery({
        relationshipId: rel.id,
        objectIds: codes,
        where: whereStr,
        outFields,
        orderByFields,
        returnGeometry: true,
      });

      return EsriUtils.queryRelatedFeatures(layer, query).pipe(
        mergeMap((res) => {
          let features = codes.flatMap((code) => res?.[code]?.features ?? []);
          if (!features?.length) {
            return of([]);
          }

          features = ArrayUtils.uniqBy(
            features,
            (x) => x.attributes['operador']
          );

          const url = EsriUtils.resolveLayerUrl(layer.url, rel.relatedTableId);

          return this.layersInfoService.getFields(url).pipe(
            map((fields) => {
              const list = new Array<FixeNetworkResultItemLike>();

              EsriUtils.mapFeaturesTo(
                features,
                fields,
                (feature, params, index) => {
                  const objectid = feature.attributes['objectid'];
                  const operatorParam = params.find(
                    (x) => x.field.name === 'operador'
                  );

                  let itemList = techFieldsCfgList
                    .map(
                      ([
                        techFieldStr,
                        downloadSpeedFieldsStr,
                        uploadSpeedFieldsStr,
                      ]) => {
                        const techParam = params.find(
                          (x) => x.field.name === techFieldStr
                        );

                        if (
                          !techParam?.value ||
                          !techParam?.valueTransformed ||
                          typeof techParam.valueTransformed !== 'string' ||
                          !techParam.valueTransformed.trim()
                        ) {
                          return null;
                        }

                        const downloadParam = params.find(
                          (x) => x.field.name === downloadSpeedFieldsStr
                        );
                        if (!downloadParam?.value) {
                          return null;
                        }

                        const uploadParam = params.find(
                          (x) => x.field.name === uploadSpeedFieldsStr
                        );
                        if (!uploadParam?.value) {
                          return null;
                        }

                        const item: FixeNetworkResultItemLike = {
                          id: objectid,
                          random: Math.random(),
                          operator: operatorParam.valueTransformed,
                          technologyCode: techParam.value,
                          technology: techParam.valueTransformed,
                          downloadSpeed: downloadParam.valueTransformed,
                          uploadSpeed: uploadParam.valueTransformed,
                        };

                        return item;
                      }
                    )
                    .filter((x) => !!x);

                  if (!itemList.length) {
                    return;
                  }

                  itemList = orderResults(itemList);

                  const item = itemList.splice(0, 1)[0];
                  item.children = itemList;

                  list.push(item);
                }
              );

              return list;
            })
          );
        })
      );
    });

    return zip(observables).pipe(
      map((res) => {
        let list = res.flat();

        return list;
      }),
      map((list) => {
        list = orderResults(list);

        return list;
      }),
      defaultIfEmpty([])
    );
  }

  private loadMobileNetworkResults(
    point: __esri.Point
  ): Observable<MobileNetworkResultItemLike[]> {
    const fieldStr = 'objectid';

    const layer = this.mobileNetworkGeomLayer;
    const query = layer.createQuery();
    query.geometry = point;
    query.num = 1;
    query.outFields = [fieldStr];
    query.returnGeometry = false;

    return EsriUtils.queryFeatures(layer, query).pipe(
      mergeMap(({ features }) => {
        if (features?.length) {
          const code = features[0].attributes[fieldStr];
          return this.listMobileResultsRelated(code);
        }

        return of([]);
      })
    );
  }

  private listMobileResultsRelated(
    code: any
  ): Observable<MobileNetworkResultItemLike[]> {
    const layer = this.mobileNetworkGeomLayer;

    const techFieldList = [
      'vel_dl_7g',
      'vel_dl_6g',
      'vel_dl_5g',
      'vel_dl_4g',
      'vel_dl_3g',
      'voz_ms_3g',
      'voz_ms_2g',
    ];

    const outFields = ['objectid', 'operador', ...techFieldList];
    const orderByFields = techFieldList.map((x) => {
      return `${x} DESC`;
    });

    const whereStr = techFieldList
      .map((x) => {
        return `(${x} is not null and ${x} <> 0)`;
      })
      .join(' or ');

    const observables = layer.relationships.map((rel) => {
      const query = new RelationshipQuery({
        relationshipId: rel.id,
        objectIds: [code],
        where: whereStr,
        outFields,
        orderByFields,
        returnGeometry: true,
      });

      return EsriUtils.queryRelatedFeatures(layer, query).pipe(
        mergeMap((res) => {
          const features = res?.[code]?.features;
          if (!features?.length) {
            return of([]);
          }

          const url = EsriUtils.resolveLayerUrl(layer.url, rel.relatedTableId);

          return this.layersInfoService.getFields(url).pipe(
            map((fields) => {
              let list = new Array<MobileNetworkResultItemLike>();

              EsriUtils.mapFeaturesTo(
                features,
                fields,
                (feature, params, index) => {
                  const objectid = feature.attributes['objectid'];
                  const operatorParam = params.find(
                    (x) => x.field.name === 'operador'
                  );

                  let itemList = techFieldList
                    .map((techFieldStr) => {
                      const techParam = params.find(
                        (x) => x.field.name === techFieldStr
                      );

                      if (
                        !techParam?.value ||
                        !techParam?.valueTransformed ||
                        typeof techParam.valueTransformed !== 'string' ||
                        !techParam.valueTransformed.trim()
                      ) {
                        return null;
                      }

                      const item: MobileNetworkResultItemLike = {
                        id: objectid,
                        random: Math.random(),
                        operator: operatorParam.valueTransformed,
                        technology: techParam.field.alias,
                        technologyCode: techParam.field.name,
                        downloadSpeed: techParam.valueTransformed,
                        downloadSpeedCode: techParam.value,
                      };

                      return item;
                    })
                    .filter((x) => !!x);

                  if (!itemList.length) {
                    return;
                  }

                  const item = itemList.splice(0, 1)[0];
                  item.children = itemList;

                  list.push(item);
                }
              );

              return list;
            })
          );
        })
      );
    });

    return zip(observables).pipe(
      map((res) => {
        let list = res.flat();

        return list;
      }),
      map((list) => {
        list = techFieldList.flatMap((techFieldStr) => {
          let auxList = list.filter((item) => {
            return item.technologyCode === techFieldStr;
          });

          auxList = orderBy(
            auxList,
            ['downloadSpeedCode', 'random'],
            ['desc', 'asc']
          );

          return auxList;
        });

        return list;
      }),
      defaultIfEmpty([])
    );
  }

  private loadSatelliteNetworkResults(
    point: __esri.Point
  ): Observable<SatelliteNetworkResultItemLike[]> {
    const layer = this.satelliteNetworkGeomLayer;
    const query = layer.createQuery();
    query.geometry = point;
    query.where = ['vel_dl_sat', 'vel_ul_sat']
      .map((x) => {
        return `(${x} is not null and ${x} <> 0)`;
      })
      .join(' and ');
    query.outFields = ['objectid', 'operador', 'vel_dl_sat', 'vel_ul_sat'];
    query.orderByFields = ['vel_dl_sat DESC', 'vel_ul_sat DESC'];
    query.returnGeometry = false;

    return EsriUtils.queryFeatures(layer, query).pipe(
      map((res) => {
        let list = new Array<SatelliteNetworkResultItemLike>();

        EsriUtils.mapFeaturesTo(
          res.features,
          res.fields,
          (feature, params, index) => {
            const operatorParam = params.find(
              (x) => x.field.name === 'operador'
            );
            const downloadSpeedParam = params.find(
              (x) => x.field.name === 'vel_dl_sat'
            );
            if (!downloadSpeedParam?.value) {
              return;
            }

            const uploadSpeedParam = params.find(
              (x) => x.field.name === 'vel_ul_sat'
            );
            if (!uploadSpeedParam?.value) {
              return;
            }

            list.push({
              id: feature.attributes['objectid'],
              random: Math.random(),
              operator: operatorParam.valueTransformed,
              downloadSpeed: downloadSpeedParam.valueTransformed,
              uploadSpeed: uploadSpeedParam.valueTransformed,
            });
          }
        );

        return list;
      }),
      map((list) => {
        list = ArrayUtils.uniqBy(list, (x) => x.operator);

        return list;
      }),
      map((list) => {
        list = orderBy(
          list,
          ['downloadSpeed', 'uploadSpeed', 'random'],
          ['desc', 'desc', 'asc']
        );

        return list;
      })
    );
  }

  private _handleFeatureLayer(url: string) {
    return new FeatureLayer({
      url: this.appConfig.normalizeUrl(url),
    });
  }
}
